import { Autocomplete,Checkbox,FormControlLabel,Stack,TextField,Tooltip,Typography } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select,{ SelectChangeEvent } from '@mui/material/Select';
import { useQuery } from '@tanstack/react-query';
import { useContext,useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { NewFormContext } from '../../context/NewFormContext';
import AttToken from '../../helpers/attToken';
import ServiceTypes,{ types } from '../../service/ServiceTypes';
import { ChecklistSubitem,ChecklistType } from '../../types/checklist/types';
import GenericAutoComplete from '../GenericAutoComplete';

const serviceTypeService = new ServiceTypes();

export default function Chamados() {
  const { t } = useTranslation('translation');
  const { modelo, setModelo } = useContext(NewFormContext);
  const [subitemSelecionado, setSubitemSelecionado] = useState<ChecklistSubitem | null>(null);

  const { data, isError, isLoading, isFetching } = useQuery(
    {
      queryKey: ['GET_SERVICE_TYPES'],
      queryFn: async () => {
        try {
          const token = await AttToken();
          if (!token) throw new Error('Token não encontrado');
          if (token) {
            const body = { ...token, empresa: token.userEmpresa };
            const result: types[] = await serviceTypeService.getAllByCompany(body);
            if (!result) throw new Error('Erro ao buscar tipos de serviço');
            return result;
          }
        } catch (error) {
          throw new Error('Erro ao buscar tipos de serviço');
        }
      },
      retry: 0,
      refetchOnWindowFocus: false,
      refetchOnMount: 'always',
    });

  if (isError) {
    toast.error(t('custom.erro_ao_buscar_chamados'), { toastId: 'custom.erro_ao_buscar_chamados' });
  }

  const onLoading = isLoading || isFetching;

  function handleDuplicados() {
    setModelo((prev: ChecklistType) => ({
      ...prev,
      ticketConfig: {
        ...prev?.ticketConfig,
        blockDuplicates: !prev?.ticketConfig?.blockDuplicates
      }
    }));
  }

  function handleServiceTypesForm(eve: string[]) {
    setModelo((prev: ChecklistType) => ({
      ...prev,
      ticketConfig: {
        ...prev.ticketConfig,
        serviceTypes: eve
      }
    }));
  }

  function handleLimiteForm(eve: React.ChangeEvent<HTMLInputElement>) {
    setModelo((prev: ChecklistType) => ({
      ...prev,
      ticketConfig: {
        ...prev.ticketConfig,
        limit: parseInt(eve.target.value)
      }
    }));
  }

  function handleMandatoryForm(eve: SelectChangeEvent<string>) {
    setModelo((prev: ChecklistType) => ({
      ...prev,
      ticketConfig: {
        ...prev.ticketConfig,
        mandatory: JSON.parse(eve.target.value)
      }
    }));
  }

  function handleServiceTypesSubitem(eve: string[]) {
    setModelo((prev: ChecklistType) => ({
      ...prev,
      itens: prev.itens.map((item) =>
        item.id === subitemSelecionado?.itemid
          ? {
            ...item,
            subitens: item.subitens.map((subitem) =>
              subitem.id === subitemSelecionado.id
                ? { ...subitem, ticketConfig: { ...subitem.ticketConfig, serviceTypes: eve } }
                : subitem
            )
          }
          : item
      )
    }));
  }

  function handleLimiteSubitem(eve: React.ChangeEvent<HTMLInputElement>) {
    setModelo((prev: ChecklistType) => ({
      ...prev,
      itens: prev.itens.map((item) =>
        item.id === subitemSelecionado?.itemid
          ? {
            ...item,
            subitens: item.subitens.map((subitem) =>
              subitem.id === subitemSelecionado.id
                ? { ...subitem, ticketConfig: { ...subitem.ticketConfig, limit: parseInt(eve.target.value) } }
                : subitem
            )
          }
          : item
      )
    }));
  }

  function handleMandatorySubitem(eve: string) {
    setModelo((prev: ChecklistType) => ({
      ...prev,
      itens: prev.itens.map((item) =>
        item.id === subitemSelecionado?.itemid
          ? {
            ...item,
            subitens: item.subitens.map((subitem) =>
              subitem.id === subitemSelecionado.id
                ? { ...subitem, ticketConfig: { ...subitem.ticketConfig, mandatory: eve } }
                : subitem
            )
          }
          : item
      )
    }));
  }

  const subitem = modelo?.itens?.find((ele) => ele.id === subitemSelecionado?.itemid)?.subitens.find((ele) => ele.id === subitemSelecionado?.id);

  function servicosAssociados(values: string[]): string[] {
    const result = data?.filter((e: types) => values.includes(e.tipoId)).map((e) => e.nome) ?? [];
    return result;
  }

  return (
    <Stack
      width='100%'
      direction="column"
      justifyContent="center"
      alignItems="center"
      spacing={2}
    >
      <Typography color='textDisabled' variant='h5' textAlign='center' fontWeight={600}>Configurações de chamados</Typography>
      <FormControlLabel
        onChange={handleDuplicados}
        control={<Checkbox checked={modelo?.ticketConfig?.blockDuplicates} />}
        label="Fechar automaticamente chamados duplicados:"
        labelPlacement='start' />
      <Typography>
        {t('formDetails.configChamados.ChamadosPor')}
      </Typography>
      <Stack
        width='100%'
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={1}
      >
        <GenericAutoComplete
          loading={onLoading}
          options={data?.map((e: types) => ({ label: e.nome, value: e.tipoId, key: e })) ?? []}
          selectedValues={modelo?.ticketConfig?.serviceTypes?.map((type) => (type)) || []}
          onChange={(values) => {
            handleServiceTypesForm(values);
          }}

          label={t('formDetails.configChamados.SelecioneOTipoformulario')}
        />
        <TextField
          disabled={onLoading}
          slotProps={{ htmlInput: { style: { textAlign: 'center' } } }}
          inputProps={{
            min: 0,
            max: 100,
            step: 1,
          }}
          label={t('formDetails.configChamados.LimiteFormulario')}
          type="number"
          value={modelo?.ticketConfig?.limit}
          onChange={handleLimiteForm}
        />
        {!modelo?.preenchimentoDeslogado
          && <FormControl sx={{ width: 250 }}>
            <InputLabel id="demo-simple-select-label">{t('formDetails.configChamados.Obrigatorio')}</InputLabel>
            <Select
              disabled={onLoading}
              label={t('formDetails.configChamados.Obrigatorio')}
              onChange={handleMandatoryForm}
              value={JSON.stringify(modelo?.ticketConfig?.mandatory)}
            >
              <MenuItem value={'true'}>{t('yes')}</MenuItem>
              <MenuItem value={'false'}>{t('no')}</MenuItem>
            </Select>
          </FormControl>}
      </Stack>
      <Typography>
        {t('formDetails.configChamados.ChamadosSub-item')}
      </Typography>
      <Autocomplete
        fullWidth
        disableClearable
        loadingText='Carregando dados...'
        isOptionEqualToValue={(option, value) => option.id === value.id}
        getOptionLabel={(option) => option.nome}
        renderOption={(props, option) => {
          const nome = option.nome;
          return (
            <Typography {...props}>
              {nome}
              {option?.ticketConfig && option?.ticketConfig?.serviceTypes?.length > 0
                && <Tooltip
                  title={
                    <div>
                      {servicosAssociados(option.ticketConfig?.serviceTypes || [])?.map((servico: string, idx: number) => (
                        <Typography
                          key={idx}
                          fontSize="small"
                        >
                          {servico}
                        </Typography>
                      ))}
                    </div>
                  }
                >
                  <Typography fontSize='small' color='textDisabled' {...props}>
                    {`(${option.ticketConfig?.serviceTypes?.length}) ${t('checklists.typesAssocieted')}`}
                  </Typography>
                </Tooltip>}
            </Typography>
          );
        }}
        options={modelo?.itens?.flatMap((item) => {
          return (
            item.subitens.map((sub) => ({
              ...sub
            }))
          );
        }) || []}
        groupBy={(option) => {
          const valor = modelo?.itens.findIndex((ele) => ele.id === option.itemid);
          return modelo?.itens[valor as number].nome || '';
        }}
        onChange={(_event, newValue: ChecklistSubitem | null) => {
          setSubitemSelecionado(newValue);
        }}
        renderInput={(params) =>
          <TextField {...params}
            variant='outlined'
            label={t('formDetails.configChamados.SelecioneSubitem')}
          />}
      />
      {subitem &&
        <Stack
          width={'100%'}
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={1}
        >
          <GenericAutoComplete
            loading={onLoading}
            options={data?.map((e: types) => ({ label: e.nome, value: e.tipoId, key: e })) ?? []}
            selectedValues={subitem.ticketConfig?.serviceTypes || []}
            onChange={handleServiceTypesSubitem}
            label={t('formDetails.configChamados.SelecioneOTipoformulario')}
          />
          <TextField
            disabled={onLoading}
            slotProps={{ htmlInput: { style: { textAlign: 'center' } } }}
            inputProps={{
              min: 0,
              max: 100,
              step: 1,
            }}
            type='number'
            label={t('formDetails.configChamados.LimiteSubitem')}
            onChange={handleLimiteSubitem}
            value={subitem.ticketConfig?.limit}
          />
          {!modelo?.preenchimentoDeslogado && <FormControl sx={{ width: 250 }}>
            <InputLabel id="demo-simple-select-label"> {t('formDetails.configChamados.Obrigatorio')}</InputLabel>
            <Select
              disabled={onLoading}
              label={t('formDetails.configChamados.Obrigatorio')}
              onChange={(eve) => handleMandatorySubitem(eve.target.value as string)}
              value={subitem?.ticketConfig?.mandatory || 'nunca'}
            >
              {subitemSelecionado?.tipo === 'avaliacao' && <MenuItem value={'naoConforme'}>{t('checklists.formModal.nonConformity')}</MenuItem>}
              {subitemSelecionado?.tipo === 'avaliacao' && <MenuItem value={'conforme'}>{t('checklists.formModal.conformity')}</MenuItem>}
              <MenuItem value={'sempre'}>{t('checklists.formModal.always')}</MenuItem>
              <MenuItem value={'nunca'}>{t('checklists.formModal.never')}</MenuItem>
            </Select>
          </FormControl>}
        </Stack>
      }
    </Stack>
  );
}
